import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  LayoutDashboard, 
  Server, 
  MessageSquare, 
  FileText, 
  Terminal, 
  Package, 
  Shield,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  History,
  LogOut,
  User,
  Settings,
  ScanSearch,
  SquareTerminal,
  BookOpen,
  Bot,
  Ticket,
  ShieldAlert,
  ShieldCheck,
  Cpu,
  Monitor,
  Network,
  BrainCircuit,
  Cloud,
  Rocket,
  Database
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const navItems = [
  { path: "/", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/servers", icon: Server, label: "Servers" },
  { path: "/terminal", icon: SquareTerminal, label: "Terminal", roles: ["admin", "senior_admin"] },
  { path: "/tasks", icon: Bot, label: "AI Tasks", roles: ["admin", "senior_admin"] },
  { path: "/helpdesk", icon: Ticket, label: "IT Helpdesk", roles: ["admin", "senior_admin"] },
  { path: "/ad", icon: ShieldAlert, label: "AD Manager", roles: ["admin", "senior_admin"] },
  { path: "/odoo", icon: Cpu, label: "Odoo AutoPilot", roles: ["admin", "senior_admin"] },
  { path: "/infrastructure", icon: Network, label: "Infrastructure", roles: ["admin", "senior_admin"] },
  { path: "/cloud", icon: Cloud, label: "Cloud (DO)", roles: ["admin", "senior_admin"] },
  { path: "/deploy", icon: Rocket, label: "Deploy Apps", roles: ["admin", "senior_admin"] },
  { path: "/backups", icon: Database, label: "Backups", roles: ["admin", "senior_admin"] },
  { path: "/autopilot-security", icon: ShieldCheck, label: "Security AutoPilot", roles: ["admin", "senior_admin"] },
  { path: "/modules", icon: Package, label: "Module Manager", roles: ["admin", "senior_admin"] },
  { path: "/odoo-ai", icon: BrainCircuit, label: "Odoo AI Module", roles: ["admin", "senior_admin"] },
  { path: "/event-logs", icon: Monitor, label: "Event Logs", roles: ["admin", "senior_admin"] },
  { path: "/runbooks", icon: BookOpen, label: "Runbooks", roles: ["admin", "senior_admin"] },
  { path: "/assistant", icon: MessageSquare, label: "AI Assistant" },
  { path: "/approvals", icon: ClipboardCheck, label: "Approvals" },
  { path: "/audit", icon: History, label: "Audit Logs", roles: ["admin", "senior_admin"] },
  { path: "/logs", icon: FileText, label: "Log Analyzer" },
  { path: "/commands", icon: Terminal, label: "Commands" },
  { path: "/apps", icon: Package, label: "App Installer" },
  { path: "/security", icon: Shield, label: "Security Audit" },
  { path: "/scans", icon: ScanSearch, label: "Security Scans", roles: ["admin", "senior_admin"] },
  { path: "/settings", icon: Settings, label: "Settings" },
];

const roleColors = {
  admin: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  senior_admin: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  junior_admin: "bg-green-500/20 text-green-400 border-green-500/30"
};

const roleLabels = {
  admin: "Admin",
  senior_admin: "Senior",
  junior_admin: "Junior"
};

export default function Sidebar({ isOpen, setIsOpen }) {
  const { user, logout } = useAuth();

  const filteredNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    return user && item.roles.includes(user.role);
  });

  return (
    <aside 
      className={`fixed left-0 top-0 h-screen bg-[#050505] border-r border-white/5 flex flex-col z-50 transition-all duration-300 ${
        isOpen ? 'w-64' : 'w-16'
      }`}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className={`p-4 border-b border-white/5 flex items-center ${isOpen ? 'justify-between' : 'justify-center'}`}>
        {isOpen && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Terminal className="w-5 h-5 text-blue-400" />
            </div>
            <span className="font-mono font-bold text-white">ServerAI</span>
          </div>
        )}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-1.5 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
          data-testid="sidebar-toggle"
        >
          {isOpen ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {/* User Info */}
      {user && isOpen && (
        <div className="p-3 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center">
              <User className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <Badge className={`text-xs border ${roleColors[user.role]}`}>
                {roleLabels[user.role]}
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                isActive
                  ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              } ${!isOpen && 'justify-center'}`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className={`w-5 h-5 flex-shrink-0 ${!isOpen && 'w-5 h-5'}`} />
            {isOpen && <span className="text-sm font-medium">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Logout Button */}
      <div className="p-3 border-t border-white/5">
        <Button
          variant="ghost"
          onClick={logout}
          className={`w-full text-zinc-400 hover:text-white hover:bg-red-500/10 ${!isOpen && 'px-0'}`}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          {isOpen && <span className="ml-2">Logout</span>}
        </Button>
      </div>

      {/* Footer */}
      {isOpen && (
        <div className="p-4 border-t border-white/5">
          <div className="text-xs text-zinc-500 font-mono">
            <div>Server Admin AI</div>
            <div className="mt-1 text-zinc-600">v1.1.0</div>
          </div>
        </div>
      )}
    </aside>
  );
}
